import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Dialog, DialogContent, Button, IconButton, Checkbox, Chip,
  Collapse, CircularProgress, Tooltip, TextField
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import DownloadIcon from '@mui/icons-material/Download'
import TuneIcon from '@mui/icons-material/Tune'
import hipersendService from '../../../services/hipersend'
import Text from '../../Text'
import { TemplatePreviewBubble } from '../../TemplatePreviewBubble'
import CreateTemplateDialog from './CreateTemplateDialog'
import {
  describeApiError, TEMPLATE_CATEGORIES, STATUS_COLORS,
  applyPlaceholders, validatePlaceholderValues,
  mergePlaceholderSpecs, pickPlaceholderValues, componentsToForm
} from '../../../libs/metaTemplates'

// snake_case exigido pela Meta: o nome pré-preenchido na personalização precisa
// entrar num formato que a validação do editor aceite (letras minúsculas, dígitos
// e "_"). Modelos da biblioteca já vêm assim, mas normalizamos por garantia.
function toSnakeCase(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')  // tira acentos (marcas combinantes do NFD)
    .replace(/[^a-z0-9]+/g, '_')      // qualquer separador vira "_"
    .replace(/^_+|_+$/g, '')          // sem "_" nas pontas
}

const CATEGORY_LABELS = TEMPLATE_CATEGORIES.reduce(
  (acc, c) => ({ ...acc, [c.value]: c.label }),
  {}
)

// Limite do cabeçalho de texto na Meta. É o teto que faz o maxLength do nome da
// empresa variar por modelo (60 menos o sufixo do header) — aqui é só pra copy.
const META_HEADER_MAX = 60

// Valores que vão pro preview: usa o que a pessoa digitou; enquanto o campo está
// em branco, cai no example do placeholder pra prévia nunca sair "quebrada" nem
// mostrar o token cru — deixando claro o que sairia na mensagem real.
function buildPreviewValues(placeholders, typed = {}) {
  const out = {}
  for (const ph of placeholders) {
    if (!ph || !ph.key) continue
    const digitado = typed[ph.key] != null ? String(typed[ph.key]) : ''
    out[ph.key] = digitado.trim() !== ''
      ? digitado
      : (ph.example != null ? String(ph.example) : '')
  }
  return out
}

// Valores dos {{n}} do corpo pra prévia: puxa o example.body_text do próprio
// modelo (nome, fatura, vencimento, valor...) pra bolha preencher as variáveis
// com os dados de exemplo — em vez de mostrar os chips {{1}}..{{4}} crus, que
// contradiriam o rótulo "Prévia com os seus dados". O envio real é quem troca
// cada {{n}} pelos dados do cliente.
function buildBodyPreviewValues(components) {
  const list = Array.isArray(components) ? components : []
  const body = list.find((c) => String(c?.type || '').toUpperCase() === 'BODY')
  const bodyText = body?.example?.body_text
  return Array.isArray(bodyText) && Array.isArray(bodyText[0]) ? bodyText[0] : []
}

/**
 * Dialog "Modelos recomendados": lista a biblioteca de templates prontos da
 * Hipersend, mostra preview de cada um e instala os selecionados na WABA da
 * conexão. Itens já instalados (nome+idioma) aparecem com chip "Instalado".
 *
 * Como os modelos compartilham os mesmos dados de marca (nome da empresa, link
 * de atendimento), o formulário é ÚNICO: a pessoa preenche uma vez, no topo, e
 * os valores valem pra todos os modelos selecionados. Por isso os valores são
 * guardados por CHAVE de placeholder ({ EMPRESA, URL_ATENDIMENTO }), e não por
 * modelo.
 */
function RecommendedTemplatesDialog({ open, onClose, api, secret, onInstalled }) {
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [library, setLibrary] = useState([])
  const [selected, setSelected] = useState(() => new Set())
  const [expandedId, setExpandedId] = useState(null)
  const [installing, setInstalling] = useState(false)
  // resultado por item após instalar: { [globalTemplateId]: { ok, message } }
  const [results, setResults] = useState({})
  // valores digitados dos placeholders, compartilhados entre TODOS os modelos:
  // { CHAVE: valor } (ex.: { EMPRESA: 'Ultrafibra', URL_ATENDIMENTO: 'https://...' })
  const [placeholderValues, setPlaceholderValues] = useState({})
  // chaves que a pessoa já mexeu — segura o erro de "obrigatório" pra não pintar
  // o campo de vermelho antes de a pessoa sequer tocar nele.
  const [touchedKeys, setTouchedKeys] = useState(() => new Set())
  // "Personalizar": abre o editor (CreateTemplateDialog) semeado com o modelo
  // já resolvido, virando um template PRÓPRIO que passa pela validação normal.
  const [personalizeOpen, setPersonalizeOpen] = useState(false)
  const [personalizeForm, setPersonalizeForm] = useState(null)

  const fetchLibrary = useCallback(async () => {
    if (!api || !secret) return
    try {
      setLoading(true)
      setLoadError(null)
      const data = await hipersendService.metaTemplates.library(api, secret)
      setLibrary(Array.isArray(data?.library) ? data.library : [])
    } catch (err) {
      setLoadError(describeApiError(err))
    } finally {
      setLoading(false)
    }
  }, [api, secret])

  // Ao abrir, zera o estado da sessão anterior e recarrega a biblioteca
  useEffect(() => {
    if (!open) return
    setSelected(new Set())
    setResults({})
    setExpandedId(null)
    setPlaceholderValues({})
    setTouchedKeys(new Set())
    fetchLibrary()
  }, [open, fetchLibrary])

  const notInstalled = useMemo(
    () => library.filter((t) => !t.installed && !results[t.globalTemplateId]?.ok),
    [library, results]
  )

  const toggleSelected = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      if (prev.size >= notInstalled.length) return new Set()
      return new Set(notInstalled.map((t) => t.globalTemplateId))
    })
  }, [notInstalled])

  const setPlaceholderField = useCallback((key, value) => {
    setPlaceholderValues((prev) => ({ ...prev, [key]: value }))
    setTouchedKeys((prev) => (prev.has(key) ? prev : new Set(prev).add(key)))
  }, [])

  // Itens selecionados (só os instaláveis contam).
  const selectedItems = useMemo(
    () => notInstalled.filter((t) => selected.has(t.globalTemplateId)),
    [notInstalled, selected]
  )

  // Conjunto de campos do formulário compartilhado: união das chaves exigidas
  // pelos itens SELECIONADOS, com o maxLength recalculado (o MENOR entre eles).
  // Muda conforme a seleção — por isso vive num useMemo.
  const sharedSpec = useMemo(
    () => mergePlaceholderSpecs(selectedItems),
    [selectedItems]
  )

  // Validação do formulário compartilhado contra o limite efetivo (o menor).
  const sharedErrors = useMemo(
    () => validatePlaceholderValues(sharedSpec, placeholderValues).errors,
    [sharedSpec, placeholderValues]
  )
  const hasSharedError = Object.keys(sharedErrors).length > 0

  const handleInstall = useCallback(async () => {
    const ids = [...selected].filter((id) => notInstalled.some((t) => t.globalTemplateId === id))
    if (ids.length === 0) return

    setInstalling(true)
    const nextResults = { ...results }

    // Instala sempre um a um, respeitando exatamente a seleção do usuário.
    // O endpoint em lote (installRecommended) instala tudo que o BACKEND vê
    // como não instalado — se a biblioteca mudou depois que o dialog abriu,
    // ele instalaria itens que o usuário nunca viu nem selecionou.
    for (const id of ids) {
      try {
        // Modelos da biblioteca compartilhada trazem placeholders [[CHAVE]] de
        // marca (empresa, link de atendimento). Manda só as chaves que ESTE
        // item declara, recortadas dos valores compartilhados.
        const item = notInstalled.find((t) => t.globalTemplateId === id)
        const phs = item?.placeholders
        const params = Array.isArray(phs) && phs.length > 0
          ? { globalTemplateId: id, placeholders: pickPlaceholderValues(phs, placeholderValues) }
          : { globalTemplateId: id }
        await hipersendService.metaTemplates.install(api, secret, params)
        nextResults[id] = { ok: true, message: null }
      } catch (err) {
        nextResults[id] = { ok: false, message: describeApiError(err) }
      }
    }

    setResults(nextResults)
    setSelected(new Set())
    setInstalling(false)

    const okCount = ids.filter((id) => nextResults[id]?.ok).length
    if (onInstalled) {
      // Mesmo sem nenhum sucesso a lista precisa recarregar: instalação que
      // falhou na Graph cria registro "failed" no master (com botão de
      // reenviar na tabela). Sem sucesso, não há mensagem de snackbar.
      onInstalled(
        okCount === 0
          ? null
          : okCount === 1
            ? '1 modelo enviado para análise da Meta.'
            : `${okCount} modelos enviados para análise da Meta.`
      )
    }
  }, [selected, notInstalled, results, api, secret, onInstalled, placeholderValues])

  // Personalizar um modelo: resolve os placeholders [[CHAVE]] com o que a pessoa
  // já digitou no formulário compartilhado, converte os components no form do
  // editor e abre o CreateTemplateDialog semeado. Vira um template PRÓPRIO (nome
  // pré-preenchido em snake_case, que o usuário ajusta) e segue pela MESMA
  // validação/instalação — não é caminho novo de risco.
  const handlePersonalize = useCallback((item) => {
    const resolvedComponents = applyPlaceholders(item.components, placeholderValues)
    const form = componentsToForm(resolvedComponents, {
      name: toSnakeCase(item.name),
      language: item.language,
      category: item.category,
    })
    setPersonalizeForm(form)
    setPersonalizeOpen(true)
  }, [placeholderValues])

  const selectedCount = selectedItems.length

  return (
    <>
    <Dialog open={open} onClose={installing ? undefined : onClose} maxWidth="md" fullWidth>
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-2">
          <AutoAwesomeIcon color="primary" />
          <div>
            <Text className="text-lg font-black">Modelos recomendados</Text>
            <Text className="text-xs font-light opacity-70">
              Templates prontos da GLA HiperWaba, ajustados para aprovação rápida na Meta
            </Text>
          </div>
        </div>
        <IconButton size="small" onClick={onClose} disabled={installing}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading && (
          <div className="flex items-center justify-center gap-3 py-12">
            <CircularProgress size={22} />
            <Text className="text-sm font-light opacity-80">Carregando biblioteca...</Text>
          </div>
        )}

        {!loading && loadError && (
          <div className="text-center py-10 px-6">
            <ErrorOutlineIcon style={{ fontSize: 34, color: STATUS_COLORS.red }} />
            <Text className="text-sm font-bold mt-2">Não foi possível carregar os modelos recomendados</Text>
            <Text className="text-xs font-light opacity-80 mt-1">{loadError}</Text>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchLibrary}
              sx={{ mt: 2 }}
            >
              Tentar novamente
            </Button>
          </div>
        )}

        {!loading && !loadError && library.length === 0 && (
          <div className="text-center py-10 px-6">
            <Text className="text-sm font-bold opacity-70">Nenhum modelo disponível na biblioteca no momento</Text>
          </div>
        )}

        {!loading && !loadError && library.length > 0 && (
          <div>
            {/* Formulário compartilhado: preenchido UMA vez, vale pra todos os
                modelos selecionados. Só aparece quando algum item selecionado
                pede personalização. */}
            {sharedSpec.length > 0 && (
              <div
                className="px-5 py-4"
                style={{
                  borderBottom: '1px solid rgba(128,128,128,0.15)',
                  backgroundColor: 'rgba(59,130,246,0.05)',
                }}
              >
                <Text className="text-sm font-black">Dados da sua empresa</Text>
                <Text className="text-xs font-light opacity-70 mt-0.5 mb-3">
                  Preencha uma vez — estes dados valem para todos os modelos selecionados.
                  A prévia de cada modelo atualiza em tempo real.
                </Text>
                <div className="flex flex-col gap-4">
                  {sharedSpec.map((ph) => {
                    const value = placeholderValues[ph.key] ?? ''
                    const err = sharedErrors[ph.key]
                    const filled = String(value).trim() !== ''
                    // Só pinta de erro depois que a pessoa mexeu no campo (ou já
                    // digitou algo inválido) — nada de vermelho num campo intacto.
                    const showErr = Boolean(err) && (filled || touchedKeys.has(ph.key))
                    const len = String(value).length
                    const over = ph.maxLength ? len > ph.maxLength : false
                    return (
                      <TextField
                        key={ph.key}
                        size="small"
                        fullWidth
                        label={ph.label || ph.key}
                        value={value}
                        onChange={(e) => setPlaceholderField(ph.key, e.target.value)}
                        placeholder={ph.example ? `ex.: ${ph.example}` : ''}
                        error={showErr}
                        disabled={installing}
                        helperText={
                          <span className="flex flex-col gap-0.5">
                            <span className="flex items-start justify-between gap-2">
                              <span>{showErr ? err : (ph.description || '')}</span>
                              {ph.maxLength ? (
                                <span
                                  style={{
                                    whiteSpace: 'nowrap',
                                    color: over ? STATUS_COLORS.red : undefined,
                                  }}
                                >
                                  {len}/{ph.maxLength}
                                </span>
                              ) : null}
                            </span>
                            {ph.maxLength ? (
                              <span className="opacity-70">
                                O cabeçalho da Meta aceita até {META_HEADER_MAX} caracteres;
                                com os modelos escolhidos, o limite deste campo é {ph.maxLength}.
                              </span>
                            ) : null}
                          </span>
                        }
                      />
                    )
                  })}
                </div>
              </div>
            )}

            {notInstalled.length > 0 && (
              <div className="flex items-center gap-1 px-3 py-2" style={{ borderBottom: '1px solid rgba(128,128,128,0.15)' }}>
                <Checkbox
                  size="small"
                  checked={selectedCount > 0 && selectedCount >= notInstalled.length}
                  indeterminate={selectedCount > 0 && selectedCount < notInstalled.length}
                  onChange={toggleAll}
                  disabled={installing}
                />
                <Text className="text-xs font-light opacity-70">
                  Selecionar todos os não instalados ({notInstalled.length})
                </Text>
              </div>
            )}

            {library.map((t) => {
              const id = t.globalTemplateId
              const result = results[id]
              const isInstalled = t.installed || result?.ok
              const isExpanded = expandedId === id
              const isSelectable = !isInstalled

              const placeholders = Array.isArray(t.placeholders) ? t.placeholders : []
              const hasPlaceholders = placeholders.length > 0
              const allFilled = placeholders.every(
                (ph) => String(placeholderValues[ph.key] ?? '').trim() !== ''
              )
              // Preview com os dados compartilhados (ou os examples enquanto vazios).
              const previewTemplate = hasPlaceholders
                ? { ...t, components: applyPlaceholders(t.components, buildPreviewValues(placeholders, placeholderValues)) }
                : t
              // Variáveis {{n}} do corpo preenchidas com os exemplos do modelo.
              const previewBodyValues = buildBodyPreviewValues(previewTemplate.components)

              return (
                <div key={id} style={{ borderBottom: '1px solid rgba(128,128,128,0.12)' }}>
                  <div className="flex items-start gap-1 px-3 py-3">
                    <Checkbox
                      size="small"
                      checked={selected.has(id) && isSelectable}
                      onChange={() => toggleSelected(id)}
                      disabled={!isSelectable || installing}
                      sx={{ mt: -0.5 }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Text className="text-sm font-bold">{t.name}</Text>
                        <Chip size="small" variant="outlined" label={t.language} sx={{ height: 20, fontSize: 11 }} />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={CATEGORY_LABELS[String(t.category || '').toUpperCase()] || t.category || '—'}
                          sx={{ height: 20, fontSize: 11 }}
                        />
                        {isInstalled && (
                          <Chip
                            size="small"
                            icon={<CheckCircleIcon style={{ fontSize: 14 }} />}
                            label="Instalado"
                            sx={{
                              height: 20,
                              fontSize: 11,
                              fontWeight: 700,
                              backgroundColor: `${STATUS_COLORS.green}1F`,
                              color: STATUS_COLORS.green,
                              '& .MuiChip-icon': { color: STATUS_COLORS.green },
                            }}
                          />
                        )}
                      </div>
                      {t.description && (
                        <Text className="text-xs font-light opacity-80 mt-1 leading-snug">
                          {t.description}
                        </Text>
                      )}
                      {result && !result.ok && (
                        <div className="flex items-start gap-1 mt-1.5">
                          <ErrorOutlineIcon style={{ fontSize: 14, color: STATUS_COLORS.red, marginTop: 2 }} />
                          <Text className="text-xs font-light" style={{ color: STATUS_COLORS.red }}>
                            {result.message}
                          </Text>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Tooltip title="Abrir o editor com este modelo já preenchido para ajustar (tirar botão, mudar texto...)" arrow>
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<TuneIcon style={{ fontSize: 15 }} />}
                          onClick={() => handlePersonalize(t)}
                          disabled={installing}
                          sx={{ fontSize: 11, minWidth: 0, whiteSpace: 'nowrap' }}
                        >
                          Personalizar
                        </Button>
                      </Tooltip>
                      <Tooltip title={isExpanded ? 'Esconder preview' : 'Ver como a mensagem chega ao cliente'} arrow>
                        <IconButton size="small" onClick={() => setExpandedId(isExpanded ? null : id)}>
                          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>

                  <Collapse in={isExpanded} unmountOnExit>
                    <div
                      className="px-6 py-5"
                      style={{
                        backgroundColor: '#0b141a',
                        backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
                        backgroundSize: '20px 20px',
                      }}
                    >
                      {hasPlaceholders && (
                        <div
                          className="text-[11px] font-light mb-2"
                          style={{ color: allFilled ? '#8fd3b0' : '#c9b98a' }}
                        >
                          {allFilled ? 'Prévia com os seus dados' : 'Prévia com dados de exemplo'}
                        </div>
                      )}
                      <div className="ml-2">
                        <TemplatePreviewBubble template={previewTemplate} values={previewBodyValues} />
                      </div>
                    </div>
                  </Collapse>
                </div>
              )
            })}
          </div>
        )}
      </DialogContent>

      <div className="flex items-center justify-between gap-3 px-5 py-3 flex-wrap">
        <div className="flex flex-col">
          <Text className="text-xs font-light opacity-70">
            Modelos instalados passam por análise da Meta antes de poder ser usados nos envios.
          </Text>
          {hasSharedError && (
            <Text className="text-xs font-light mt-0.5" style={{ color: STATUS_COLORS.orange }}>
              Complete os dados da sua empresa acima para liberar a instalação.
            </Text>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="small" onClick={onClose} disabled={installing}>
            Fechar
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={installing ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
            disabled={installing || selectedCount === 0 || hasSharedError}
            onClick={handleInstall}
          >
            {installing
              ? 'Instalando...'
              : `Instalar selecionados${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
          </Button>
        </div>
      </div>
    </Dialog>

    {/* Editor semeado da personalização. Ao instalar, o próprio editor fecha e
        avisa o pai (onInstalled recarrega a lista); fechamos também o recomendado
        pra pessoa ver o novo modelo já na tela. */}
    <CreateTemplateDialog
      open={personalizeOpen}
      onClose={() => setPersonalizeOpen(false)}
      api={api}
      secret={secret}
      initialForm={personalizeForm}
      title="Personalizar modelo"
      onInstalled={(message) => {
        setPersonalizeOpen(false)
        if (onInstalled) onInstalled(message)
        onClose()
      }}
    />
    </>
  )
}

export default RecommendedTemplatesDialog
